/**
 * utils/economicCalendar.ts
 * Pure utility functions for Economic Calendar domain
 *
 * Parametric by design: all functions accept data as parameters rather than
 * importing from `constants/economicCalendar`. This maintains utils purity per CODING_STANDARDS
 * and ensures functions remain testable in isolation.
 *
 * USAGE:
 * - Import country-to-currency mapping from `constants/economicCalendar` at call sites
 * - Pass mapping as function arguments
 * - Functions are pure: same inputs always produce same outputs
 *
 * @example
 * ```ts
 * // Import mapping from constants (at call site, not in this file)
 * import { COUNTRY_TO_CURRENCY } from '@/constants/economicCalendar';
 * import { getCurrencyForCountry } from '@/utils/economicCalendar';
 *
 * // Pass mapping as argument (parametric pattern)
 * const currency = getCurrencyForCountry('US', COUNTRY_TO_CURRENCY);
 * // => 'USD'
 * ```
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero imports from `constants/` or other domain modules
 * - No side effects (no logs, no mutations, no I/O, no caches)
 * - All functions are deterministic and testable
 * - **This file must not import constants** (parametric design requirement)
 */

/**
 * Get the currency code for a country
 *
 * Parametric lookup: accepts mapping as argument to maintain purity.
 * Returns the mapped currency code or undefined if country not found.
 *
 * @param country - Country code to lookup (e.g., "US", "GB", "EU")
 * @param mapping - Country-to-currency mapping to search
 * @returns Currency code (e.g., "USD") or undefined if not found
 *
 * @example
 * ```ts
 * import { COUNTRY_TO_CURRENCY } from '@/constants/economicCalendar';
 *
 * getCurrencyForCountry('US', COUNTRY_TO_CURRENCY);  // => 'USD'
 * getCurrencyForCountry('GB', COUNTRY_TO_CURRENCY);  // => 'GBP'
 * getCurrencyForCountry('EU', COUNTRY_TO_CURRENCY);  // => 'EUR'
 * getCurrencyForCountry('ZZ', COUNTRY_TO_CURRENCY);  // => undefined
 * ```
 */
export function getCurrencyForCountry(
  country: string,
  mapping: Record<string, string>
): string | undefined {
  return mapping[country];
}

/**
 * Get flag emoji for a country code
 *
 * Converts a 2-letter country code to its corresponding Unicode flag emoji.
 * Uses regional indicator symbols (U+1F1E6 to U+1F1FF) which combine to form flags.
 *
 * @param countryCode - 2-letter country code (e.g., "US", "GB", "EU")
 * @returns Flag emoji (e.g., "🇺🇸") or empty string if input is invalid
 *
 * @example
 * ```ts
 * getCountryFlagEmoji('US');  // => '🇺🇸'
 * getCountryFlagEmoji('GB');  // => '🇬🇧'
 * getCountryFlagEmoji('EU');  // => '🇪🇺'
 * getCountryFlagEmoji('');    // => ''
 * getCountryFlagEmoji('123'); // => ''
 * ```
 */
export function getCountryFlagEmoji(countryCode: string): string {
  // Validate input: must be exactly 2 uppercase A-Z characters
  if (!countryCode || countryCode.length !== 2) {
    return '';
  }

  const upperCode = countryCode.toUpperCase();

  // Check all characters are A-Z
  for (const char of upperCode) {
    const charCode = char.charCodeAt(0);
    if (charCode < 65 || charCode > 90) {
      return '';
    }
  }

  // Convert to regional indicator symbols
  // A-Z maps to U+1F1E6 to U+1F1FF
  const codePoints = [...upperCode].map(
    (char) => 0x1f1e6 + char.charCodeAt(0) - 65
  );

  return String.fromCodePoint(...codePoints);
}
